/*
 * Sensivo - Halloween 2026: search easter egg (gift chooser).
 * Loaded (type=module, deferred) by snippets/snv-halloween-body.liquid only when the easter egg gate is
 * on (snippets/snv-halloween-egg-gate.liquid). Everything (keyword, products, collection, code,
 * threshold, texts) comes from #hwg-config, i.e. Theme settings > Halloween Easter Egg.
 *
 * Flow: exact keyword in any search box -> dialog with 2 gifts -> server-side eligibility
 * (sections/halloween-eligibility.liquid) -> /cart/add.js (+ hidden _halloween_gift property)
 * -> discount code on the cart (/cart/update.js) -> theme cart drawer refreshed through the theme's
 * own CartLinesUpdateEvent (@shopify/events). Any failure rolls the cart back.
 *
 * Cart change detection: the theme already emits CartLinesUpdateEvent for every add / change / remove
 * (product form, cart drawer, cart page, quick add). We listen to that event and re-check eligibility
 * once its promise settles - no fetch interceptor needed. Plus one check on page load (cart edited in
 * another tab). These checks are UX only; the real protection is the Shopify discount.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

const root = document.documentElement;
const cfg = readJSON('hwg-config') || {};
const text = cfg.text || {};
const products = Array.isArray(cfg.products) ? cfg.products : [];
const GIFT_PROP = '_halloween_gift';
const ownEvents = new WeakSet();

function readJSON(id) {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Gates                                                               */
/* ------------------------------------------------------------------ */

/** Halloween layer on (html[data-hw]) and before the end date (cached HTML may outlive it). */
function isActive() {
  if (!root.hasAttribute('data-hw') || !cfg.keyword) return false;
  const end = cfg.endAt ? Date.parse(cfg.endAt) : NaN;
  return Number.isNaN(end) || Date.now() < end;
}

/** The 18+ gate (snippets/age-verification.liquid) sets cookie snv_age_ok=1 when passed. */
function ageVerified() {
  if (!/(?:^|;\s*)snv_age_ok=1(?:;|$)/.test(document.cookie)) return false;
  const gate = document.getElementById('snv-age-gate');
  return !gate || !gate.classList.contains('snv-age-gate--visible');
}

const anyGiftAvailable = () => products.some((p) => p.available);
const canOpen = () => isActive() && ageVerified() && anyGiftAvailable();
const normalize = (value) => String(value || '').trim().toLowerCase();
const matchesKeyword = (value) => !!cfg.keyword && normalize(value) === normalize(cfg.keyword);

/* ------------------------------------------------------------------ */
/* Search interception (header inline, search modal, /search page,     */
/* search blocks; Enter and the mobile "Go" key)                       */
/* ------------------------------------------------------------------ */

// Horizon declares `Theme` with const in a classic script: a global lexical binding, not window.Theme.
// eslint-disable-next-line no-undef
const themeRoutes = () => (typeof Theme !== 'undefined' && Theme?.routes) || {};
const searchPath = () => themeRoutes().search_url || '/search';

function isSearchForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  try {
    return new URL(form.action, location.href).pathname === searchPath();
  } catch {
    return false;
  }
}

function searchInputFrom(target) {
  if (!(target instanceof HTMLInputElement) || target.name !== 'q') return null;
  return isSearchForm(target.form) ? target : null;
}

function trigger(input, event) {
  event.preventDefault();
  event.stopImmediatePropagation();
  input.value = '';
  // Clear predictive results and close the search modal with the theme's own components.
  const predictive = input.closest('predictive-search-component');
  try {
    predictive?.resetSearch?.(false);
  } catch {}
  const searchDialog = input.closest('dialog-component');
  input.blur(); // closes the virtual keyboard on phones
  if (searchDialog && typeof searchDialog.closeDialog === 'function') {
    // The search modal restores focus to its own trigger on close; that becomes our focus-return target.
    Promise.resolve(searchDialog.closeDialog()).finally(() => openModal(null));
  } else {
    openModal(input);
  }
}

// Capture phase on WINDOW: runs before Horizon's delegated on:keydown / on:input handlers, which are
// registered on document (assets/component.js) - predictive-search.js navigates on Enter by itself.
window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = searchInputFrom(event.target);
    if (input && matchesKeyword(input.value) && canOpen()) trigger(input, event);
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!isSearchForm(form)) return;
    const input = form.querySelector('input[name="q"]');
    if (input && matchesKeyword(input.value) && canOpen()) trigger(input, event);
  },
  true
);

// No live suggestions for the exact keyword.
window.addEventListener(
  'input',
  (event) => {
    const input = searchInputFrom(event.target);
    if (input && matchesKeyword(input.value) && canOpen()) event.stopImmediatePropagation();
  },
  true
);

/* ------------------------------------------------------------------ */
/* Cart helpers                                                        */
/* ------------------------------------------------------------------ */

const rootUrl = () => window.Shopify?.routes?.root || '/';
const addUrl = () => themeRoutes().cart_add_url || `${rootUrl()}cart/add.js`;
const updateUrl = () => (themeRoutes().cart_update_url ? `${themeRoutes().cart_update_url}.js` : `${rootUrl()}cart/update.js`);

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) throw new Error(data.description || data.message || `HTTP ${res.status}`);
  return data;
}

async function getCart() {
  const res = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) throw new Error(`cart ${res.status}`);
  return res.json();
}

/** Server-side eligible subtotal. Never assumes the threshold is met: any failure throws. */
async function getEligibility() {
  const url = `${rootUrl()}?sections=${encodeURIComponent(cfg.sectionId)}&hwg=${Date.now()}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) throw new Error(`eligibility ${res.status}`);
  const html = (await res.json())[cfg.sectionId];
  const node = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-hwg-eligibility]');
  const data = JSON.parse(node?.textContent || 'null');
  if (!data || typeof data.active !== 'boolean') throw new Error('eligibility payload');
  return data;
}

const giftLines = (cart) => (cart.items || []).filter((item) => item.properties && item.properties[GIFT_PROP]);
const discountCodes = (cart) => (cart.discount_codes || []).map((d) => d.code).filter(Boolean);

async function setDiscountCodes(codes) {
  return postJSON(updateUrl(), {
    discount: [...new Set(codes)].join(','),
    sections: cartSectionIds().join(','),
  });
}

/**
 * Puts the easter egg code on the cart, keeping codes already there (e.g. a product discount code).
 * Primary: /cart/update.js { discount }. Fallback when that response carries no discount_codes:
 * the /discount/<code> route (session cookie) with a redirect to the light /cart.js endpoint,
 * so the page never navigates. Throws unless Shopify reports the code as applicable.
 */
async function applyCode(existingCodes) {
  let cart = await setDiscountCodes([...existingCodes, cfg.code]);
  if (!Array.isArray(cart.discount_codes)) {
    const res = await fetch(`${rootUrl()}discount/${encodeURIComponent(cfg.code)}?redirect=${encodeURIComponent(`${rootUrl()}cart.js`)}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`discount ${res.status}`);
    cart = await setDiscountCodes(discountCodes(await getCart()));
  }
  const applied = (cart.discount_codes || []).find((d) => normalize(d.code) === normalize(cfg.code));
  if (!applied || applied.applicable === false) {
    const others = existingCodes.filter((c) => normalize(c) !== normalize(cfg.code));
    // With another code on the cart, the usual cause is a code that does not combine with the gift.
    throw Object.assign(new Error('discount not applicable'), { conflict: others.length > 0 });
  }
  return cart;
}

/** Removes lines; with `codes` it also replaces the cart's discount codes in the same request. */
async function removeLines(keys, codes) {
  const updates = {};
  keys.forEach((key) => {
    updates[key] = 0;
  });
  const body = { updates, sections: cartSectionIds().join(',') };
  if (codes) body.discount = [...new Set(codes)].join(',');
  return postJSON(updateUrl(), body);
}

/** Re-render the cart drawer / cart page through the theme's own event (same path as product-form.js). */
function announceCart(action, cart, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
  ownEvents.add(event);
  document.body.dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: cart.sections,
      items: cart.items,
      itemCount: cart.item_count,
      source: 'snv-halloween-gift',
      didError: false,
    },
  });
  deferred.promise.then(lockSoon, () => {});
}

/**
 * Gift lines are rendered with .hwg-qty-locked and server-disabled +/- (snippets/cart-products.liquid),
 * but Horizon's quantity selector captures that state only once (connectedCallback) and re-enables the
 * buttons after a drawer morph. Re-assert the lock after every cart render.
 */
function lockGiftQuantities() {
  document.querySelectorAll('.hwg-qty-locked').forEach((wrapper) => {
    wrapper.querySelectorAll('cart-quantity-selector-component, quantity-selector-component').forEach((selector) => {
      selector.serverDisabledMinus = true;
      selector.serverDisabledPlus = true;
    });
    wrapper.querySelectorAll('.quantity-plus, .quantity-minus, input[type="number"]').forEach((el) => {
      el.disabled = true;
    });
  });
}
const lockSoon = () => requestAnimationFrame(() => requestAnimationFrame(lockGiftQuantities));

/** Opens the theme's cart drawer (when the page has one) with its own API. */
function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen && !drawer.hasAttribute('open')) drawer.open();
}

/* ------------------------------------------------------------------ */
/* Analytics (only systems already on the page, consent respected)     */
/* ------------------------------------------------------------------ */

function track(name, params = {}) {
  if (!cfg.analytics) return;
  try {
    const privacy = window.Shopify?.customerPrivacy;
    const allowed = (fn) => !privacy || typeof privacy[fn] !== 'function' || privacy[fn]();
    if (allowed('analyticsProcessingAllowed')) {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...params });
    }
    if (allowed('marketingAllowed')) {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, params);
      if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['track', name, params]);
    }
  } catch {}
}

/* ------------------------------------------------------------------ */
/* Toast (short, polite live region)                                   */
/* ------------------------------------------------------------------ */

let toastEl = null;
let toastTimer = 0;
async function toast(message) {
  if (!message) return;
  await ensureStyles(); // never show the popover with the browser's default (centred) styles
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'hwg-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    // A manual popover lives in the top layer: shown after the (modal) cart drawer opens, it sits above
    // it and is not inert, so it is visible and announced. Without popover support: plain fixed box.
    if ('showPopover' in HTMLElement.prototype) toastEl.setAttribute('popover', 'manual');
    document.body.appendChild(toastEl);
  }
  if (toastEl.hasAttribute('popover')) {
    try {
      if (toastEl.matches(':popover-open')) toastEl.hidePopover();
      toastEl.showPopover(); // re-show = moved to the top of the top layer
    } catch {}
  }
  toastEl.textContent = message;
  toastEl.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastEl.classList.remove('is-visible');
    try {
      if (toastEl.hasAttribute('popover') && toastEl.matches(':popover-open')) toastEl.hidePopover();
    } catch {}
  }, 5000);
}

/* ------------------------------------------------------------------ */
/* Dialog                                                              */
/* ------------------------------------------------------------------ */

let stylesPromise = null;
function ensureStyles() {
  if (!stylesPromise) {
    stylesPromise = new Promise((resolve) => {
      const href = document.querySelector('script[src*="snv-halloween-gift.js"]')?.src.replace(/snv-halloween-gift\.js/, 'snv-halloween-gift.css');
      if (!href) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = link.onerror = () => resolve();
      document.head.appendChild(link);
    });
  }
  return stylesPromise;
}

const fill = (template, values) =>
  String(template || '').replace(/\{(suma|prag)\}/g, (_, key) => values[key] ?? '');

let dialog = null;
let opener = null;
let busy = false;

function buildDialog() {
  dialog = document.createElement('dialog');
  dialog.className = 'hwg';
  dialog.setAttribute('aria-labelledby', 'hwg-title');
  dialog.setAttribute('aria-describedby', 'hwg-subtitle');
  dialog.innerHTML = `
    <div class="hwg__panel">
      <button type="button" class="hwg__close"><span aria-hidden="true">&times;</span></button>
      <svg class="hwg__spider" aria-hidden="true" focusable="false" viewBox="0 0 64 64"><use href="#hw-spider"></use></svg>
      <h2 class="hwg__title" id="hwg-title"></h2>
      <p class="hwg__subtitle" id="hwg-subtitle"></p>
      <div class="hwg__status" hidden>
        <p class="hwg__message" role="status" aria-live="polite"></p>
        <div class="hwg__progress" hidden><span class="hwg__progress-bar"></span></div>
        <a class="button-secondary hwg__collection" hidden></a>
      </div>
      <ul class="hwg__cards" role="list"></ul>
    </div>`;
  dialog.querySelector('.hwg__close').setAttribute('aria-label', text.close || '');
  dialog.querySelector('.hwg__title').textContent = text.title || '';
  dialog.querySelector('.hwg__subtitle').textContent = text.subtitle || '';
  const link = dialog.querySelector('.hwg__collection');
  link.textContent = text.collectionButton || '';
  if (cfg.collectionUrl) link.href = cfg.collectionUrl;

  const list = dialog.querySelector('.hwg__cards');
  products.forEach((product) => {
    const li = document.createElement('li');
    li.className = 'hwg__card';
    li.dataset.productId = String(product.productId);
    if (product.image) {
      const img = document.createElement('img');
      img.className = 'hwg__img';
      img.src = product.image.src;
      img.srcset = `${product.image.src} 1x, ${product.image.src2x} 2x`;
      img.width = product.image.width;
      img.height = product.image.height;
      img.alt = product.image.alt || product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      li.appendChild(img);
    }
    const title = document.createElement('h3');
    title.className = 'hwg__card-title';
    title.textContent = product.title || '';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button hwg__choose';
    button.addEventListener('click', () => chooseGift(product, button));
    const note = document.createElement('p');
    note.className = 'hwg__card-note';
    li.append(title, button, note);
    list.appendChild(li);
  });

  dialog.querySelector('.hwg__close').addEventListener('click', () => dialog.close());
  // Click on the backdrop (outside the panel) closes.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => {
    unlockScroll(dialog);
    root.removeAttribute('data-hwg-open');
    const back = opener;
    opener = null;
    if (back && back.isConnected && typeof back.focus === 'function' && back.getClientRects().length) back.focus();
  });
  document.body.appendChild(dialog);
}

/**
 * @param {'loading'|'ready'|'below'|'none'|'error'|'off'} state
 * @param {object} [elig]
 * @param {string} [message]
 */
function render(state, elig, message) {
  const status = dialog.querySelector('.hwg__status');
  const msg = dialog.querySelector('.hwg__message');
  const progress = dialog.querySelector('.hwg__progress');
  const link = dialog.querySelector('.hwg__collection');
  const values = { suma: elig?.remainingFormatted, prag: elig?.thresholdFormatted };
  const giftIds = new Set((elig?.gifts || []).map((g) => String(g.productId)));
  const blocked = state !== 'ready';

  dialog.setAttribute('aria-busy', String(state === 'loading'));
  status.hidden = !(state === 'below' || state === 'none' || state === 'error' || message);
  msg.textContent =
    message ||
    (state === 'below' ? fill(text.below, values) : state === 'none' ? fill(text.noEligible, values) : state === 'error' ? text.error || '' : '');
  progress.hidden = !(state === 'below' || state === 'none');
  if (!progress.hidden) {
    const pct = elig && elig.threshold > 0 ? Math.min(100, Math.round((elig.subtotal / elig.threshold) * 100)) : 0;
    progress.style.setProperty('--hwg-progress-scale', String(pct / 100));
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('aria-valuemax', '100');
    progress.setAttribute('aria-valuenow', String(pct));
    progress.setAttribute('aria-label', text.collectionButton || '');
  }
  link.hidden = !(state === 'below' || state === 'none') || !cfg.collectionUrl;

  products.forEach((product) => {
    const card = dialog.querySelector(`.hwg__card[data-product-id="${product.productId}"]`);
    if (!card) return;
    const button = card.querySelector('.hwg__choose');
    const note = card.querySelector('.hwg__card-note');
    const inCart = giftIds.has(String(product.productId));
    button.textContent = text.button || '';
    button.removeAttribute('aria-busy');
    note.textContent = !product.available ? text.soldOut || '' : inCart ? text.already || '' : '';
    button.disabled = blocked || busy || !product.available || inCart;
    card.classList.toggle('is-unavailable', !product.available);
    card.classList.toggle('is-chosen', inCart);
  });
}

function stateFor(elig) {
  if (!elig.active) return 'off';
  if (elig.subtotal >= elig.threshold && elig.eligibleLines > 0) return 'ready';
  return elig.eligibleLines > 0 ? 'below' : 'none';
}

async function refreshDialog() {
  render('loading');
  try {
    const elig = await getEligibility();
    const state = stateFor(elig);
    if (state === 'off') {
      render('error', elig);
      return;
    }
    render(state, elig);
    if (state === 'below' || state === 'none') track('halloween_gift_blocked_min_cart');
  } catch {
    render('error');
    track('halloween_gift_error', { stage: 'eligibility' });
  }
}

async function openModal(from) {
  if (!canOpen()) return;
  // Never on top of another open dialog (search modal, cart drawer, Trick or Treat popup...).
  const other = document.querySelector('dialog:modal:not(.hwg)');
  if (other && typeof other.close === 'function') other.close();
  await ensureStyles();
  if (!dialog) buildDialog();
  if (dialog.open) return;
  opener = from instanceof HTMLElement ? from : document.activeElement instanceof HTMLElement ? document.activeElement : null;
  root.setAttribute('data-hwg-open', '');
  dialog.showModal(); // native: focus trap, inert page, ESC
  lockScroll(dialog);
  dialog.querySelector('.hwg__close').focus();
  track('halloween_gift_modal_open');
  refreshDialog();
}

/* ------------------------------------------------------------------ */
/* Choosing a gift                                                     */
/* ------------------------------------------------------------------ */

async function chooseGift(product, button) {
  if (busy) return;
  busy = true;
  button.setAttribute('aria-busy', 'true');
  dialog.querySelectorAll('.hwg__choose').forEach((b) => (b.disabled = true));

  let addedKey = null;
  let removedGifts = [];
  let codesBefore = null;
  let codeAdded = false;
  try {
    const [elig, cart] = await Promise.all([getEligibility(), getCart()]);
    const state = stateFor(elig);
    if (state !== 'ready') {
      busy = false;
      if (state === 'off') render('error', elig);
      else render(state, elig);
      track('halloween_gift_blocked_min_cart');
      return;
    }

    const existing = giftLines(cart);
    if (existing.some((line) => String(line.product_id) === String(product.productId))) {
      busy = false;
      render('ready', elig, text.already);
      return;
    }
    if (existing.length) {
      removedGifts = existing;
      await removeLines(existing.map((line) => line.key));
    }

    const added = await postJSON(addUrl(), {
      items: [{ id: product.variantId, quantity: 1, properties: { [GIFT_PROP]: '1' } }],
    });
    addedKey = added.items?.[0]?.key || null;

    codesBefore = discountCodes(cart);
    let updated;
    if (cfg.code) {
      codeAdded = true;
      updated = await applyCode(codesBefore);
    } else {
      updated = await setDiscountCodes(codesBefore);
    }

    busy = false;
    dialog.close();
    announceCart('add', updated, [{ merchandiseId: String(product.variantId), quantity: 1 }]);
    // The drawer opens on add only when the theme's "auto open" setting is on; the gift flow always opens it.
    openCartDrawer();
    toast(text.success);
    track('halloween_gift_selected', { product_id: String(product.productId) });
  } catch (error) {
    // Roll back: no ambiguous cart. Remove the gift we added, restore the previous gift and codes.
    try {
      if (addedKey) await removeLines([addedKey]);
      if (removedGifts.length) {
        await postJSON(addUrl(), {
          items: removedGifts.map((line) => ({ id: line.variant_id, quantity: line.quantity, properties: line.properties })),
        });
      }
      if (codeAdded || addedKey || removedGifts.length) {
        const cart = await setDiscountCodes(codesBefore ?? discountCodes(await getCart()));
        announceCart('update', cart, []);
      }
    } catch {}
    busy = false;
    render('error', null, error?.conflict ? text.conflict || text.error : text.error);
    track('halloween_gift_error', { stage: error?.conflict ? 'code_conflict' : 'add' });
    console.warn('[hwg]', error);
  }
}

/* ------------------------------------------------------------------ */
/* Keep the cart honest after every change                             */
/* ------------------------------------------------------------------ */

let checking = false;
let recheck = false;

async function enforceCart() {
  if (!isActive()) return;
  if (checking) {
    recheck = true;
    return;
  }
  checking = true;
  try {
    const elig = await getEligibility();
    if (!elig.active || !elig.gifts.length) return;
    const tooMuch = elig.gifts.some((g) => g.quantity > 1) || elig.gifts.length > 1;
    if (stateFor(elig) !== 'ready') {
      // Drop the gift code too: left on the cart it would only show as "not applicable".
      const codes = cfg.code ? discountCodes(await getCart()).filter((c) => normalize(c) !== normalize(cfg.code)) : undefined;
      const cart = await removeLines(elig.gifts.map((g) => g.key), codes);
      announceCart('remove', cart, elig.gifts.map((g) => ({ id: g.key, quantity: 0 })));
      toast(fill(text.removed, { prag: elig.thresholdFormatted }));
      track('halloween_gift_removed_auto');
    } else if (tooMuch) {
      // Quantity is locked to 1 in the cart UI; repair anything that slipped through (e.g. old tab).
      const [keep, ...rest] = elig.gifts;
      const updates = { [keep.key]: 1 };
      rest.forEach((g) => (updates[g.key] = 0));
      const cart = await postJSON(updateUrl(), {
        updates,
        sections: cartSectionIds().join(','),
      });
      announceCart('update', cart, []);
    }
  } catch {
    // Network error: do nothing (the Shopify discount still protects the checkout).
  } finally {
    checking = false;
    if (recheck) {
      recheck = false;
      enforceCart();
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownEvents.has(event)) return;
  const done = (result) => {
    lockSoon();
    // The theme resolves its event with the full cart items: no gift line, nothing to check.
    const items = result?.detail?.items;
    if (Array.isArray(items) && !items.some((item) => item?.properties?.[GIFT_PROP])) return;
    enforceCart();
  };
  if (event.promise && typeof event.promise.then === 'function') event.promise.then(done, () => {});
  else done();
});

// Only when the server rendered a gift line in the cart: no extra request on normal page views.
function initialCheck() {
  lockGiftQuantities();
  if (!isActive() || !cfg.giftInCart) return;
  const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
  idle(() => enforceCart(), { timeout: 4000 });
}
if (document.readyState === 'complete') initialCheck();
else window.addEventListener('load', initialCheck, { once: true });
window.addEventListener('pageshow', (event) => {
  if (event.persisted) enforceCart();
});

// Landing directly on /search?q=<keyword> (e.g. a search form we could not intercept).
// At the end of the module: openModal() needs every declaration above (no TDZ).
if (location.pathname === searchPath()) {
  const params = new URLSearchParams(location.search);
  if (matchesKeyword(params.get('q')) && canOpen()) {
    params.delete('q');
    const qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? `?${qs}` : '') + location.hash);
    document.querySelectorAll('input[name="q"]').forEach((i) => {
      if (matchesKeyword(i.value)) i.value = '';
    });
    openModal(null);
  }
}
